import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { InvoicesKeys } from "@/features/invoices/api/invoices";
import {
  recurringInvoiceKeys,
  recurringInvoicesApi,
} from "@/features/invoices/api/recurringInvoices";
import {
  CreateRecurringInvoiceInput,
  RecurringInvoiceStatus,
  UpdateRecurringInvoiceInput,
} from "@/features/invoices/types/recurring";

interface UseRecurringInvoicesOptions {
  enabled?: boolean;
}

export const useRecurringInvoices = (
  status?: RecurringInvoiceStatus | RecurringInvoiceStatus[],
  options?: UseRecurringInvoicesOptions,
) => {
  return useQuery({
    queryKey: recurringInvoiceKeys.list(status),
    queryFn: () => recurringInvoicesApi.getAll(status),
    enabled: options?.enabled ?? true,
  });
};

export const useRecurringInvoice = (id: string | null) => {
  return useQuery({
    queryKey: recurringInvoiceKeys.detail(id || ""),
    queryFn: () => {
      if (!id) {
        throw new Error("Recurring invoice ID is required");
      }
      return recurringInvoicesApi.getById(id);
    },
    enabled: !!id,
    retry: 1,
    staleTime: 30000, // Cache for 30 seconds
  });
};

export const useCreateRecurringInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateRecurringInvoiceInput) =>
      recurringInvoicesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: recurringInvoiceKeys.lists(),
      });
      queryClient.invalidateQueries({ queryKey: [InvoicesKeys.Invoices] });
      toast.success("Recurring invoice created successfully");
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Failed to create recurring invoice",
      );
    },
  });
};

export const useUpdateRecurringInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateRecurringInvoiceInput;
    }) => recurringInvoicesApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: recurringInvoiceKeys.detail(variables.id),
      });
      queryClient.invalidateQueries({
        queryKey: recurringInvoiceKeys.lists(),
      });
      toast.success("Recurring invoice updated successfully");
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Failed to update recurring invoice",
      );
    },
  });
};

export const useCancelRecurringInvoice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: recurringInvoicesApi.cancel,
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: recurringInvoiceKeys.detail(id),
      });
      queryClient.invalidateQueries({
        queryKey: recurringInvoiceKeys.lists(),
      });
      toast.success("Recurring invoice canceled successfully");
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Failed to cancel recurring invoice",
      );
    },
  });
};
